import { ReauthenticationDialog, useCountdown } from "@aurem/components";
import { useUserData } from "graphql/user";
import { useSendEmailOtp } from "hooks/employee";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useState,
} from "react";
import { VerificationRequestActionType } from "types/graphql/graphql";

export type ContextType = {
  openReauthDialog: (options: {
    isMfaEnabled?: boolean;
    actionType?: VerificationRequestActionType;
    onSuccess: (
      value: string,
      setError: (msg: string) => void,
    ) => Promise<void>;
  }) => void;
};

export type Props = {
  children: ReactNode;
};

export const ReauthDialogContext = createContext<ContextType | undefined>(
  undefined,
);

export const ReauthenticationProvider = ({ children }: Props) => {
  const { user } = useUserData();
  const email = user?.email ?? "";
  const [open, setOpen] = useState(false);
  const [isMfaEnabled, setIsMfaEnabled] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [actionType, setActionType] = useState<
    VerificationRequestActionType | undefined
  >(undefined);
  const { sendEmailOtp } = useSendEmailOtp();
  const resendCountdown = useCountdown();
  const [onSuccessCallback, setOnSuccessCallback] = useState<
    | ((
        value: string,
        setError: (msg: string) => void,
      ) => Promise<boolean | void>)
    | null
  >(null);

  const handleSendEmailOtp = useCallback(async () => {
    if (actionType) {
      await sendEmailOtp(actionType);
      resendCountdown.reset();
    }
  }, [actionType, sendEmailOtp, resendCountdown]);

  const openReauthDialog = useCallback(
    ({
      isMfaEnabled,
      actionType,
      onSuccess,
    }: {
      isMfaEnabled?: boolean;
      actionType?: VerificationRequestActionType;
      onSuccess: (
        value: string,
        setError: (msg: string) => void,
      ) => Promise<void>;
    }) => {
      setIsMfaEnabled(!!isMfaEnabled);
      setOnSuccessCallback(() => onSuccess);
      setErrorMessage(undefined);
      setActionType(actionType);
      setOpen(true);
    },
    [],
  );

  const handleSuccess = async (value: string) => {
    let hasError = false;

    const wrappedSetError = (msg: string) => {
      hasError = true;
      setErrorMessage(msg);
    };

    if (onSuccessCallback) {
      await onSuccessCallback(value, wrappedSetError);
      if (!hasError) {
        setOpen(false);
      }
    }
  };

  const dialogType = isMfaEnabled ? "mfa" : "email-otp";

  return (
    <ReauthDialogContext.Provider value={{ openReauthDialog }}>
      {children}
      <ReauthenticationDialog
        open={open}
        onOpenChange={setOpen}
        type={dialogType}
        onSuccess={handleSuccess}
        errorMessage={errorMessage}
        actionType={actionType}
        handleSendEmailOtp={handleSendEmailOtp}
        resendCountdown={resendCountdown}
      />
    </ReauthDialogContext.Provider>
  );
};

export function useReauthenticationDialog() {
  const context = useContext(ReauthDialogContext);
  if (!context) {
    throw new Error(
      "useReauthenticationDialog must be used within ReauthenticationProvider",
    );
  }
  return context;
}
